/**
 * The gate — nothing renders behind it until we know who is here.
 *
 * ## Why a gate rather than a banner
 *
 * Every surface in this app is per-user: the workspace *is* the layout, the
 * journal is positions, the screener's Trades groups are somebody's plans. With
 * no identity there is nothing to draw, so a shell rendered behind a sign-in
 * prompt would spend a WebGL context and two sockets building a chart it must
 * then throw away.
 *
 * It also removes an ordering problem rather than managing one. `persistence`
 * loads a workspace by user id on mount; mounting it before Firebase has
 * answered means loading the *fallback* user's workspace and then discovering
 * the real one — two loads, and a visible flash of somebody else's layout.
 *
 * ## Three states, and the middle one is the one that gets forgotten
 *
 * `checking` is not cosmetic. Firebase restores a session from storage
 * asynchronously, so an app that treated "no user yet" as "signed out" would
 * show this screen to a returning user on every single load, for the tenth of a
 * second before it corrected itself.
 *
 * ## Its own theme variables, because it is outside the theme
 *
 * This screen renders *instead of* `ThemeProvider`'s subtree, so no `--qf-*`
 * variable exists above it — and a `var()` with nothing behind it is not an
 * error, it is `transparent`. The submit button was white text on a white card
 * and the fields had no borders, so nobody found the button and Enter was the
 * only way in. `SCREEN` therefore carries the brand's default theme itself.
 *
 * ## The password field carries its own two escapes
 *
 * **Show / Hide** flips the `type` on one element rather than swapping two, so
 * the value, the caret and a password manager's grip on the field all survive
 * it. It is `type="button"` — a bare button inside a form submits the form,
 * which would attempt a sign-in every time somebody checked what they typed.
 *
 * **Forgot password?** says the same sentence for an address with an account
 * and one without. Anything else is an oracle for who has an account here,
 * asked from a screen that needs no session to reach; `lib/firebase.ts`
 * swallows `user-not-found` so the two answers cannot come apart. It is offered
 * only while signing *in*: an account that does not exist yet has no password
 * to reset.
 *
 * ## A Descope build shows Descope's flow, and one question after it
 *
 * The brand's icon and name, then `sign-up-or-in` in the brand's tenant
 * (`lib/descope.ts`) — the fields, codes and passkey ceremony are Descope's,
 * styled in its console. Everything below about the password field is the
 * Firebase screen, which stays until its users have moved.
 *
 * After a sign-in **made here** — never a restored session — an account with
 * no passkey on a device that can make one is asked once, "Add a passkey?",
 * and the answer is kept per account in this browser.
 *
 * ## Unconfigured, this never appears
 *
 * With no `VITE_DESCOPE_PROJECT_ID` and no `VITE_FIREBASE_*` the gate resolves
 * immediately to "signed in as the fallback user" and renders its children. That is what keeps a fresh clone
 * working with no accounts, no project and no network.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { themeCssVariables } from "@qufolio/model";

import { authAvailable, trackUser } from "../lib/user";
import { BRAND } from "../lib/brand-current";
import { brandDefaultTheme } from "../lib/brand-themes";
import {
  PROVIDERS,
  createAccountWithPassword,
  sendPasswordReset,
  signInWithGoogle,
  signInWithPassword,
} from "../lib/firebase";
import { authProvider } from "../lib/session";
import {
  DESCOPE_FLOW,
  PASSKEY_DOMAIN,
  PASSKEY_FLOW,
  adoptSession,
  canAddPasskey,
  descopeProjectId,
  descopeStyle,
  descopeTenant,
} from "../lib/descope";
import { UpdateBar } from "./UpdateBar";
import { SignInBackdrop } from "./SignInBackdrop";

type State = "checking" | "in" | "out" | "passkey" | "error";

export function SignIn({ children }: { children: ReactNode }) {
  // Unconfigured builds start resolved. Not "in" via an effect — a first paint
  // of the checking screen would flash on every load of a dev app that has no
  // Firebase at all, which is most of them.
  const [state, setState] = useState<State>(authAvailable ? "checking" : "in");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Separate from `error`, because "check your inbox" is not a failure and
  // showing it in the failure colour is how somebody reads it as one.
  const [notice, setNotice] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Off by default: the field is masked because somebody may be behind you.
  // Never persisted, and reset by nothing — a reveal lasts as long as the
  // screen it was asked for on.
  const [reveal, setReveal] = useState(false);
  // A fresh project has no users at all, so the first person through has to be
  // able to make one — otherwise the app cannot be opened until somebody adds a
  // row by hand in the Firebase console.
  const [creating, setCreating] = useState(false);
  // Set by the flow's `success`, so the passkey offer follows a sign-in made
  // here and never a session restored from storage on an ordinary page load.
  const fresh = useRef(false);

  useEffect(() => {
    if (!authAvailable) return;
    return trackUser(
      (uid) => {
        if (!uid) return setState("out");
        if (!fresh.current) return setState("in");
        fresh.current = false;
        void offerPasskey(uid).then((offer) => setState(offer ? "passkey" : "in"));
      },
      (message) => {
        setError(message);
        setState("error");
      },
    );
  }, []);

  if (state === "in") return <>{children}</>;

  if (authProvider === "descope") {
    return (
      <div style={SCREEN}>
        <UpdateBar overlay />
        {BACKDROP && <SignInBackdrop name={BACKDROP} />}
        {/* Bare while the flow shows: it draws its own panel, and ours around
            it read as a box in a box. */}
        <div style={state === "out" ? BARE : CARD}>
          {/* The flow draws the brand's logo from its Descope style, so ours
              would be a second one; every other state here has only ours. */}
          {state !== "out" && <BrandMark />}
          {state === "checking" && <p style={MUTED}>Checking your session…</p>}
          {state === "out" && (
            <DescopeFlow
              onSuccess={() => {
                fresh.current = true;
                setError(null);
                adoptSession().catch((cause: unknown) => setError(describe(cause)));
              }}
            />
          )}
          {state === "passkey" && <PasskeyOffer onDone={() => setState("in")} />}
          {state === "error" && (
            <button type="button" onClick={() => window.location.reload()} style={BUTTON}>
              Try again
            </button>
          )}
          {error && <p style={ERROR}>{error}</p>}
        </div>
      </div>
    );
  }

  const attempt = (run: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    run()
      // No `setState("in")` anywhere: `trackUser` is the single source of that.
      .catch((cause: unknown) => {
        const message = describe(cause);
        // A popup the user dismissed is not an error worth shouting about.
        if (/popup-closed|cancelled-popup/.test(message)) return;
        setError(explain(message));
      })
      .finally(() => setBusy(false));
  };

  const withPassword = (event: React.FormEvent) => {
    event.preventDefault();
    attempt(() =>
      creating ? createAccountWithPassword(email, password) : signInWithPassword(email, password),
    );
  };

  const withGoogle = () => {
    attempt(signInWithGoogle);
  };

  /**
   * Email a reset link.
   *
   * Not routed through `attempt`: the answer is a **notice** rather than a
   * session, and the one thing that must not vary is the sentence. It says the
   * same thing for an address with an account and one without — `firebase.ts`
   * swallows `user-not-found` for that reason — because the difference would be
   * an oracle for who has an account here, asked from a screen with no session.
   */
  const resetPassword = () => {
    setError(null);
    setNotice(null);
    const address = email.trim();
    if (!address) {
      setError("Type your email address above first, then ask for the link.");
      return;
    }
    setBusy(true);
    sendPasswordReset(address)
      .then(() => setNotice(`If ${address} has an account, a reset link is on its way.`))
      .catch((cause: unknown) => setError(explain(describe(cause))))
      .finally(() => setBusy(false));
  };

  return (
    <div style={SCREEN}>
      {/* A deploy can land while somebody sits here; the bar floats over the
          top edge and inherits `SCREEN`'s theme variables. See `UpdateBar`. */}
      <UpdateBar overlay />
      {BACKDROP && <SignInBackdrop name={BACKDROP} />}
      <div style={CARD}>
        <h1 style={TITLE}>{BRAND.identity.name}</h1>

        {state === "checking" ? (
          <p style={MUTED}>Checking your session…</p>
        ) : (
          <>
            <p style={MUTED}>Sign in to load your workspace.</p>

            {PROVIDERS.has("password") && (
              <form onSubmit={withPassword} style={FORM}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  autoComplete="username"
                  required
                  style={FIELD}
                />
                <div style={FIELD_WRAP}>
                  <input
                    type={reveal ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    // Tells a password manager which of the two this is, so it
                    // offers to save on create and to fill on sign-in. It stays
                    // right through a reveal: the `type` changes, the meaning
                    // of the field does not, and a manager that lost track of
                    // it would stop offering to fill.
                    autoComplete={creating ? "new-password" : "current-password"}
                    required
                    style={REVEALABLE_FIELD}
                  />
                  <button
                    type="button"
                    onClick={() => setReveal((was) => !was)}
                    // `aria-pressed` rather than a label that only changes: a
                    // screen reader then announces the state of one control
                    // instead of two controls appearing in turn.
                    aria-pressed={reveal}
                    aria-label={reveal ? "Hide password" : "Show password"}
                    title={reveal ? "Hide password" : "Show password"}
                    style={REVEAL_BUTTON}
                  >
                    {reveal ? "Hide" : "Show"}
                  </button>
                </div>
                <button type="submit" disabled={busy} style={BUTTON}>
                  {busy ? "Please wait…" : creating ? "Create account" : "Log in"}
                </button>
                <div style={LINK_ROW}>
                  <button
                    type="button"
                    onClick={() => {
                      setCreating((was) => !was);
                      setError(null);
                      setNotice(null);
                    }}
                    style={LINK}
                  >
                    {creating ? "I already have an account" : "Create an account"}
                  </button>
                  {/* Only while signing in: an account that does not exist yet
                      has no password to reset, and offering it there would send
                      somebody to their inbox to wait for nothing. */}
                  {!creating && (
                    <button type="button" onClick={resetPassword} disabled={busy} style={LINK}>
                      Forgot password?
                    </button>
                  )}
                </div>
              </form>
            )}

            {PROVIDERS.has("google") && (
              <button type="button" onClick={withGoogle} disabled={busy} style={BUTTON}>
                {busy ? "Signing in…" : "Continue with Google"}
              </button>
            )}

            {error && <p style={ERROR}>{error}</p>}
            {notice && <p style={NOTICE}>{notice}</p>}
          </>
        )}
      </div>
    </div>
  );
}

function describe(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/**
 * Firebase's own wording for the errors a person can act on.
 *
 * `auth/operation-not-allowed` is the one that matters: it means the provider is
 * switched **off in the console**, and Firebase's text sends people to read
 * their own code instead of the setting that is actually wrong.
 */
function explain(message: string): string {
  if (/operation-not-allowed/.test(message))
    return "That sign-in method is not enabled for this Firebase project. Console → Authentication → Sign-in method.";
  if (/invalid-credential|wrong-password|user-not-found/.test(message))
    return "That email and password do not match an account.";
  if (/email-already-in-use/.test(message))
    return "That email already has an account — log in instead.";
  if (/weak-password/.test(message)) return "Passwords need at least six characters.";
  return message;
}

/** The brand's icon and name, around the flow but never over it (it draws its own). */
function BrandMark() {
  return (
    <>
      {/* The build writes a brand's icon to a fixed name (`vite.config.ts`'s
          BRAND_ICON); a brand without one keeps the app's. */}
      <img
        src={BRAND.identity.icon ? "/brand-icon.png" : "/icon-512.png"}
        alt=""
        width={48}
        height={48}
        style={ICON}
      />
      <h1 style={TITLE}>{BRAND.identity.name}</h1>
    </>
  );
}

/**
 * A Descope flow in this brand's tenant and style — `sign-up-or-in` by default.
 *
 * Created imperatively into a host `div` rather than as JSX: React 18 neither
 * types a custom element nor wires its custom events, and only `success` and
 * `error` matter. The script is a lazy import, for `firebase.ts`'s reason —
 * nobody signed in pays for it.
 */
function DescopeFlow({
  flowId = DESCOPE_FLOW,
  onSuccess,
  onError,
}: {
  flowId?: string;
  onSuccess: () => void;
  onError?: (message: string) => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const succeeded = useRef(onSuccess);
  succeeded.current = onSuccess;
  const failedWith = useRef(onError);
  failedWith.current = onError;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let flow: HTMLElement | null = null;
    let cancelled = false;
    import("@descope/web-component")
      .then(() => {
        if (cancelled || !host.current) return;
        flow = document.createElement("descope-wc");
        flow.setAttribute("project-id", descopeProjectId);
        flow.setAttribute("flow-id", flowId);
        flow.setAttribute("tenant", descopeTenant);
        flow.setAttribute("style-id", descopeStyle);
        // A backdrop is dark whatever the brand's plain background is —
        // TrendStrat's is white and it wears SweeterTrade's.
        flow.setAttribute("theme", BACKDROP || isDark(BRAND.identity.color.background) ? "dark" : "light");
        flow.addEventListener("success", () => succeeded.current());
        flow.addEventListener("error", (event) => {
          const detail = (event as unknown as CustomEvent<{ errorDescription?: string; errorMessage?: string }>).detail;
          failedWith.current?.(detail?.errorDescription || detail?.errorMessage || "Something went wrong.");
        });
        host.current.appendChild(flow);
      })
      .catch(() => setFailed(true));
    return () => {
      cancelled = true;
      flow?.remove();
    };
  }, [flowId]);

  if (failed) return <p style={ERROR}>The sign-in form did not load. Check your connection and reload.</p>;
  return <div ref={host} style={FLOW} />;
}

/**
 * Where "already offered" is kept, per account **and passkey domain**, in this
 * browser. The domain is in the key because a passkey made for one domain is
 * useless on the next: when a brand moved to its own project (2026-10-11), an
 * offer shown — and refused by the browser — under the old domain had to be
 * asked again, not remembered.
 */
const OFFERED = (uid: string) => `qufolio-passkey-offered:${PASSKEY_DOMAIN}:${uid}`;

/**
 * Whether to show "Add a passkey?" once, after this account's first sign-in
 * here.
 *
 * Once means once: the flag is written when the offer is *shown*, so "Not now"
 * is an answer rather than a postponement. Storage that throws reads as
 * offered — asking on every sign-in in a private window is the worse failure.
 */
async function offerPasskey(uid: string): Promise<boolean> {
  try {
    if (localStorage.getItem(OFFERED(uid))) return false;
  } catch {
    return false;
  }
  const offer = await canAddPasskey().catch(() => false);
  if (offer) {
    try {
      localStorage.setItem(OFFERED(uid), new Date().toISOString());
    } catch {
      // Shown anyway; it may be shown again, which is the cheaper failure.
    }
  }
  return offer;
}

/**
 * Our words above Descope's `add-passkeys` screen, whose two buttons are the
 * offer's.
 *
 * **The click has to land inside the flow.** Safari creates a passkey only
 * straight after a click, and a button of ours would start the flow and then
 * wait on a network round trip before asking — far enough from the click to
 * be refused. It is also why passkeys need no SDK access: Trigger Method can
 * stay *Disable API & SDK*.
 *
 * Both buttons end the flow in DONE (read off the flow, 2026-10-10), so
 * `success` is either answer and opens the app.
 */
function PasskeyOffer({ onDone }: { onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <p style={HEADING}>Add a passkey?</p>
      <p style={{ ...MUTED, textAlign: "center" }}>
        Next time, sign in with your fingerprint, face or device PIN — no code to wait for.
      </p>
      <DescopeFlow flowId={PASSKEY_FLOW} onSuccess={onDone} onError={setError} />
      {error && <p style={ERROR}>{error}</p>}
    </>
  );
}

/** `#rrggbb` darker than mid-grey, for the flow's `theme`. */
function isDark(hex: string): boolean {
  const match = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!match) return false;
  const [r, g, b] = match.slice(1).map((part) => parseInt(part, 16) / 255);
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b! < 0.5;
}

/**
 * Signing out, for the toolbar. Absent when there is nobody to sign out.
 *
 * Renders nothing rather than a disabled control: a dev build with no Firebase
 * has no session, and a greyed-out button would suggest one exists.
 */
export function SignOutButton({ onSignOut }: { onSignOut: () => void }) {
  if (!authAvailable) return null;
  return (
    <button type="button" onClick={onSignOut} style={LINK} title="Sign out">
      Sign out
    </button>
  );
}

/** The brand's sign-in backdrop, drawn under the card; see `SignInBackdrop`. */
const BACKDROP = BRAND.identity.signInBackdrop;

const SCREEN: React.CSSProperties = {
  // The brand's default theme, because nothing above this screen defines one —
  // see "Its own theme variables" at the top. Once, at load: the brand is fixed
  // at build time, and the workspace that might name another theme is exactly
  // what this screen is waiting to load.
  ...(themeCssVariables(brandDefaultTheme()) as React.CSSProperties),
  position: "fixed",
  inset: 0,
  display: "grid",
  placeItems: "center",
  background: "var(--qf-background)",
  color: "var(--qf-text)",
};

const CARD: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.9rem",
  padding: "2.4rem 2.8rem",
  minWidth: 320,
  maxWidth: 380,
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 8,
  boxShadow: "0 16px 48px rgba(0,0,0,0.45)",
  // Over the backdrop, which is absolutely placed under it.
  position: "relative",
  zIndex: 1,
};

/** {@link CARD}'s layout without its panel, for the Descope flow. */
const BARE: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: CARD.gap,
  minWidth: CARD.minWidth,
  maxWidth: CARD.maxWidth,
  position: CARD.position,
  zIndex: CARD.zIndex,
};

const TITLE: React.CSSProperties = {
  margin: 0,
  fontSize: "1.35rem",
  fontWeight: 660,
  letterSpacing: "-0.02em",
};

const ICON: React.CSSProperties = { borderRadius: 10 };

/** The passkey offer's question — a step down from the brand name above it. */
const HEADING: React.CSSProperties = { margin: 0, fontSize: "1.05rem", fontWeight: 600 };

/** The flow draws its own fields and buttons; this only gives it the card's width. */
const FLOW: React.CSSProperties = { width: "100%" };

const MUTED: React.CSSProperties = {
  margin: 0,
  fontSize: "0.9rem",
  color: "var(--qf-text-secondary)",
};

const BUTTON: React.CSSProperties = {
  marginTop: "0.2rem",
  padding: "0.55rem 1.1rem",
  background: "var(--qf-accent)",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  font: "inherit",
  fontWeight: 600,
  cursor: "pointer",
};

const FORM: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.55rem",
  width: "100%",
};

const FIELD: React.CSSProperties = {
  padding: "0.5rem 0.65rem",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  font: "inherit",
  fontSize: "0.9rem",
};

/** The password field and its reveal button, which sits inside the box. */
const FIELD_WRAP: React.CSSProperties = {
  position: "relative",
  display: "flex",
  width: "100%",
};

const REVEALABLE_FIELD: React.CSSProperties = {
  ...FIELD,
  // Room for the button, so a long password never runs underneath it.
  paddingRight: "3.6rem",
  width: "100%",
  boxSizing: "border-box",
};

const REVEAL_BUTTON: React.CSSProperties = {
  position: "absolute",
  top: 0,
  bottom: 0,
  right: 0,
  padding: "0 0.65rem",
  background: "transparent",
  border: "none",
  color: "var(--qf-text-secondary)",
  font: "inherit",
  fontSize: "0.78rem",
  cursor: "pointer",
};

const LINK_ROW: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: "0.75rem",
};

const LINK: React.CSSProperties = {
  padding: 0,
  background: "transparent",
  border: "none",
  color: "var(--qf-text-secondary)",
  font: "inherit",
  fontSize: "0.8rem",
  cursor: "pointer",
  textDecoration: "underline",
};

const ERROR: React.CSSProperties = {
  margin: 0,
  fontSize: "0.82rem",
  color: "var(--qf-negative, #e5484d)",
  maxWidth: "22rem",
  textAlign: "center",
};

/** `ERROR`'s shape, none of its alarm — a reset link that was sent is good news. */
const NOTICE: React.CSSProperties = {
  ...ERROR,
  color: "var(--qf-text-secondary)",
};
